import React, { FC, useContext } from 'react';
import { FormValuesContext, ViewportContext } from '../../index.contexts';
import {
  DESTINATION_HIDDEN_FIELDS,
  DATES,
  ROOMS_AND_GUESTS,
  DESTINATION,
  SPECIAL_RATES,
} from '../../../constants/StoreConstants';
import { getDateFormatted, getValueFromDate } from '../../../utils/DateUtils';
import { DATE_FORMAT_VALUE, DATE_FORMAT_YEAR_MONTH_DAY } from '../../../constants/ApplicationConstants';
import UnknownFields from './unknownFields';
import { useRouter } from 'next/router';

import moment from 'moment';
import { changeCountryCodeForChina } from '../../../../../../utils/src/helper';

declare global {
  interface Window {
    destinationMetaDataDetails: {
      isStateEnabled: '';
      isCountryEnabled: '';
      state: '';
      country: '';
      destinationName: '';
    };
  }
}

const HiddenFields: FC<{ isPublishUserSelection?: boolean }> = ({ isPublishUserSelection = false }) => {
  const { formValues } = useContext(FormValuesContext);
  const isMobileView = useContext(ViewportContext);
  const hiddenFieldsValues = formValues?.[DESTINATION_HIDDEN_FIELDS];
  const splRatesHiddenFields = formValues?.[SPECIAL_RATES];
  const router = useRouter();
  const { propertyId } = router.query;
  const { displayText: destinationName, pid, secondaryDescription } = formValues?.[DESTINATION] ?? {};
  const getRooms = formValues?.roomsAndGuests?.roomsCount;
  let destination = '';

  if (window?.destinationMetaDataDetails) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { isStateEnabled, isCountryEnabled, state, country, destinationName } = window?.destinationMetaDataDetails;
    const stateValue = state ? `, ${state}` : '';
    const countryValue = country ? `, ${country}` : '';

    destination = `${destinationName}${stateValue}${countryValue}`;

    if (isStateEnabled) {
      destination = `${destinationName}${countryValue}`;
    } else if (isCountryEnabled) {
      destination = `${destinationName}`;
    }
  }

  /** Add dates value computation */
  const datesValues = formValues?.[DATES] ?? {};
  const { fromDate, toDate } = datesValues;

  const { flexible } = datesValues;
  const numberOfNights = Math.ceil(toDate?.diff(fromDate, 'days', true) ?? 0);
  //changing format as these date values are not being used anywhere else but were creating issue on homepage / Adv Search Page / RLM
  const fromDateValue = getValueFromDate(fromDate, DATE_FORMAT_VALUE);
  const toDateValue = getValueFromDate(toDate, DATE_FORMAT_VALUE);
  const fromToDateValue = toDateValue;
  const { roomsCount, adultsCount, childrenCount, childrenAges } = formValues?.[ROOMS_AND_GUESTS] ?? {};
  const childrenAgesString =
    typeof childrenAges == 'object'
      ? childrenAges?.map((age: string) => (isNaN(parseInt(age, 10)) ? 0 : age)).toString()
      : childrenAges
          ?.split(',')
          ?.map((age: string) => (isNaN(parseInt(age, 10)) ? 0 : age))
          .toString();
  const roomsCountValue = roomsCount ? parseInt(roomsCount, 10).toString() : '';
  return (
    <>
      {/* PArent wrapper cannot have longer DOM's. Loading unnecessary fields */}
      <UnknownFields />
      <input type="hidden" name="recordsPerPage" value="40" />
      <input
        type="hidden"
        name="destinationAddress.types"
        value={hiddenFieldsValues?.types ?? ''}
        className="destination-types"
      />
      <input
        type="hidden"
        name="destinationAddress.latitude"
        value={hiddenFieldsValues?.latitude ?? ''}
        className="search-latitude"
      />
      {isPublishUserSelection && (getRooms === '10-25' || getRooms === '26+') ? (
        <input type="hidden" name="destinationAddress.destination" value={destination} />
      ) : (
        ''
      )}
      {isMobileView ? (
        <input
          type="hidden"
          name="destinationAddress.destination"
          value={destinationName ? destinationName : destination}
        />
      ) : (
        ''
      )}
      <input type="hidden" name="destinationAddress.location" value={destinationName ? destinationName : destination} />
      <input
        type="hidden"
        name="destinationAddress.stateProvince"
        value={hiddenFieldsValues?.state ?? ''}
        className="search-state js-search-state destinationAddress.stateProvince"
      />
      <input
        type="hidden"
        name="destinationAddress.region"
        value={hiddenFieldsValues?.region ?? ''}
        className="search-region js-search-region destinationAddress.region"
      />
      <input
        type="hidden"
        name="searchRadius"
        value={hiddenFieldsValues?.radius ?? ''}
        className="searchRadius js-search-radius"
      />
      <input
        type="hidden"
        name="destinationAddress.placeId"
        value={pid ?? hiddenFieldsValues?.placeId ?? ''}
        className="search-place-id"
      />
      <input
        type="hidden"
        name="destinationAddress.country"
        value={hiddenFieldsValues?.country ? changeCountryCodeForChina(hiddenFieldsValues?.country) : ''}
        className="search-country js-search-country destinationAddress.country"
      />
      <input
        type="hidden"
        name="destinationAddress.address"
        value={hiddenFieldsValues?.address ?? ''}
        className="destination-address"
      />
      <input
        type="hidden"
        name="destinationAddress.secondaryText"
        value={
          secondaryDescription ?? (hiddenFieldsValues?.state && hiddenFieldsValues?.country)
            ? `${hiddenFieldsValues?.state}, ${hiddenFieldsValues?.country}`
            : ''
        }
        className="search-secondary-description"
      />
      <input
        type="hidden"
        name="destinationAddress.postalCode"
        value={hiddenFieldsValues?.postalAddress ?? ''}
        className="destination-postal-code"
      />
      <input
        type="hidden"
        name="destinationAddress.city"
        value={hiddenFieldsValues?.city ?? ''}
        className="search-city js-search-city hotel-city-airport"
      />
      <input
        type="hidden"
        name="destinationAddress.mainText"
        value={hiddenFieldsValues?.primaryDescription ?? hiddenFieldsValues?.city ?? ''}
        className="search-primary-description"
      />
      <input type="hidden" name="isTransient" value="true" />
      <input
        type="hidden"
        name="destinationAddress.longitude"
        value={hiddenFieldsValues?.longitude ?? ''}
        className="search-longitude"
      />
      <input
        type="hidden"
        name="destinationAddress.type"
        value={hiddenFieldsValues?.destinationType ?? ''}
        className="search-destinationtype"
      />
      <input type="hidden" name="initialRequest" value="true" />
      <input
        type="hidden"
        name="destinationAddress.website"
        value={hiddenFieldsValues?.website ?? ''}
        className="destination-website"
      />
      <input
        type="hidden"
        name="fromToDate"
        value={moment(formValues?.[DATES]?.lastDefaultDate)?.format('MM/DD/YYYY') ?? ''}
      />
      <input type="hidden" name="fromToDate_submit" value={fromToDateValue ?? ''} />
      <input type="hidden" name="fromDate" value={fromDateValue ?? ''} />
      <input type="hidden" name="toDate" value={toDateValue ?? ''} />
      <input type="hidden" name="toDateDefaultFormat" value={toDateValue ?? ''} />
      <input type="hidden" name="fromDateDefaultFormat" value={fromDateValue ?? ''} />
      <input type="hidden" name="flexibleDateSearch" value={flexible ?? ''} />
      <input type="hidden" name="isHideFlexibleDateCalendar" value="false" />
      <input type="hidden" name="t-start" value={getDateFormatted(fromDate, DATE_FORMAT_YEAR_MONTH_DAY) ?? ''} />
      <input type="hidden" name="t-end" value={getDateFormatted(toDate, DATE_FORMAT_YEAR_MONTH_DAY) ?? ''} />
      <input type="hidden" name="isFlexibleDatesOptionSelected" value={flexible ?? ''} />
      <input type="hidden" name="lengthOfStay" value={numberOfNights ?? ''} />
      <input type="hidden" name="roomCount" value={roomsCountValue ?? ''} />
      <input type="hidden" name="numAdultsPerRoom" value={adultsCount ?? ''} />
      <input type="hidden" name="childrenCount" value={childrenCount ?? ''} />
      <input type="hidden" name="childrenAges" value={childrenAgesString ?? ''} />

      <input type="hidden" name="clusterCode" value={splRatesHiddenFields?.clusterCode ?? ''} />
      <input type="hidden" name="corporateCode" value={splRatesHiddenFields?.corporateCode ?? ''} />
      <input type="hidden" name="numberOfRooms" value={roomsCountValue ?? ''} />
      <input type="hidden" name="propertyCode" value={propertyId} />
    </>
  );
};

export default HiddenFields;
